
import React, { useMemo } from 'react';
import { AdvancedSearchSelect, SearchItem } from './AdvancedSearchSelect';
import { Bike, Battery, MapPin, User, Hash } from 'lucide-react';

interface BikeData {
  id: string;
  model: string;
  serial_number?: string;
  gps_tag?: string;
  assigned_courier?: {
    name: string;
    id: string;
  };
  status?: string;
  battery_percentage?: number;
  rental_number?: string;
}

interface BikeSearchSelectProps {
  bikes: BikeData[];
  value?: string;
  onValueChange: (bikeId: string, bike?: BikeData) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const BikeSearchSelect: React.FC<BikeSearchSelectProps> = ({
  bikes,
  value,
  onValueChange,
  placeholder = "Поиск велосипеда по модели, GPS тегу, курьеру, номеру аренды...",
  className,
  disabled
}) => {
  // Преобразуем данные велосипедов в формат SearchItem
  const searchItems = useMemo((): SearchItem[] => {
    return bikes.map(bike => {
      // Формируем метаданные для каждого велосипеда
      const metadata: Record<string, string> = {};
      
      // Добавляем GPS тег если есть
      if (bike.gps_tag) {
        metadata['GPS'] = bike.gps_tag;
      }
      
      // Добавляем курьера если назначен
      if (bike.assigned_courier) {
        metadata['Курьер'] = bike.assigned_courier.name;
      }
      
      // Добавляем уровень батареи если доступен
      if (bike.battery_percentage !== undefined) {
        metadata['Батарея'] = `${bike.battery_percentage}%`;
      }

      // Добавляем номер аренды если есть - только строки
      if (bike.rental_number && typeof bike.rental_number === 'string' && bike.rental_number.trim()) {
        metadata['Аренда'] = bike.rental_number;
      }

      // Формируем secondaryText - приоритет: номер аренды, серийный номер, GPS тег
      let secondaryText = '';
      if (bike.rental_number && typeof bike.rental_number === 'string' && bike.rental_number.trim()) {
        secondaryText = `Аренда: ${bike.rental_number}`;
      } else if (bike.serial_number) {
        secondaryText = `S/N: ${bike.serial_number}`;
      } else if (bike.gps_tag) {
        secondaryText = `GPS: ${bike.gps_tag}`;
      } else {
        secondaryText = 'Без активной аренды';
      }
      
      return {
        id: bike.id,
        primaryText: bike.model,
        secondaryText,
        type: 'bike' as const,
        status: bike.assigned_courier ? 'rented' : 'available',
        icon: <Bike className="h-4 w-4" />,
        metadata
      };
    });
  }, [bikes]);
  
  // Функция группировки по статусу
  const groupBy = (item: SearchItem) => {
    return item.status === 'rented' ? 'В аренде' : 'Доступные';
  };
  
  // Расширенные поля поиска
  const searchFields = ['primaryText', 'secondaryText'];
  
  // Кастомный рендер элемента - используем уже подготовленные данные
  const renderItem = (item: SearchItem, searchQuery: string) => {
    const bike = bikes.find(b => b.id === item.id);
    if (!bike) return null;
    
    const highlightMatch = (text: string, query: string) => {
      if (!query) return text;
      const regex = new RegExp(`(${query})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, index) => 
        regex.test(part) ? 
          <mark key={index} className="bg-yellow-200 px-1 rounded text-black">{part}</mark> : 
          part
      );
    };
    
    return (
      <div className="flex items-center space-x-3 p-3">
        <Bike className="h-5 w-5 text-blue-500" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">
            {highlightMatch(bike.model, searchQuery)}
          </div>
          <div className="text-xs text-muted-foreground">
            {highlightMatch(item.secondaryText, searchQuery)}
          </div>
          <div className="flex items-center gap-3 mt-1">
            {bike.gps_tag && (
              <div className="flex items-center gap-1 text-xs">
                <MapPin className="h-3 w-3" />
                {highlightMatch(bike.gps_tag, searchQuery)}
              </div>
            )}
            {bike.assigned_courier && (
              <div className="flex items-center gap-1 text-xs">
                <User className="h-3 w-3" />
                {highlightMatch(bike.assigned_courier.name, searchQuery)}
              </div>
            )}
            {bike.battery_percentage !== undefined && (
              <div className="flex items-center gap-1 text-xs">
                <Battery className="h-3 w-3" />
                {bike.battery_percentage}%
              </div>
            )}
          </div>
        </div>
        <div className="text-xs">
          {bike.assigned_courier ? (
            <span className="text-orange-600">В аренде</span>
          ) : (
            <span className="text-green-600">Доступен</span>
          )}
        </div>
      </div>
    );
  };
  
  const handleValueChange = (id: string, item?: SearchItem) => {
    const bike = bikes.find(b => b.id === id);
    onValueChange(id, bike);
  };
  
  return (
    <AdvancedSearchSelect
      items={searchItems}
      value={value}
      onValueChange={handleValueChange}
      placeholder={placeholder}
      searchFields={searchFields}
      groupBy={groupBy}
      renderItem={renderItem}
      className={className}
      disabled={disabled}
      maxHeight="400px"
    />
  );
};
